import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Modal } from 'antd';
import { Formik } from 'formik';
import { cloneDeep } from 'lodash-es';

import { addErrorMessage, DebounceSelect, FormField, FormikFieldTouched } from 'components/common';
import { QUERY_INFINITY } from 'lib/constants/number';
import { CHARACTER_SIZE } from 'lib/global';
import useI18n from 'lib/hooks/useI18n';
import { Role } from 'lib/models';
import Parse from 'lib/parse';
import { nameVerify, roleNameReg } from 'lib/regExp';
import { FormError } from 'lib/types/error';
import { getUserOptionsByName, getUserOptionsByValues } from 'lib/users';

type ValueProps = {
  objectId?: string;
  name?: string;
  users?: string[];
  role?: string;
  tag?: string;
};
/* 由于Role表中的name不可修改且输入内容有限制，所以加了个tag字段用于显示名称，name显示为唯一标识 */
type SelectListProps = { disabled: boolean; id: string; pId: string; title: string; value: string }[];

interface ModalProps {
  visible: boolean;
  loading: boolean;
  onSubmit: (values: ValueProps) => void;
  onCancel: () => void;
  initialValues: ValueProps;
  width: number;
  title: string;
  selectList: SelectListProps;
}

export const getChildren = async (id: string): Promise<{ id: string }[]> => {
  const children = await new Parse.Query(Parse.Role)
    .equalTo('isSystem', false)
    .limit(QUERY_INFINITY)
    .equalTo('roles', Parse.Role.createWithoutData(id))
    .find();
  return children;
};

export const getAllChild = async (list: { id: string }[], arr: string[]): Promise<void> => {
  list.forEach(v => arr.push(v.id));
  const query = list.map(v =>
    new Parse.Query(Parse.Role)
      .equalTo('isSystem', false)
      .limit(QUERY_INFINITY)
      .equalTo('roles', Parse.Role.createWithoutData(v.id)),
  );
  if (query.length) {
    const results = await Parse.Query.or(...query).find();
    if (results.length) {
      await getAllChild(results, arr);
    }
  }
};

export const getNewList = (oldList: SelectListProps, arr: string[]): SelectListProps => {
  const newList = cloneDeep(oldList);
  newList.forEach(v => (v.disabled = arr.includes(v.id)));
  return newList;
};

const RolesModal: React.FC<ModalProps> = ({
  visible,
  onSubmit,
  onCancel,
  loading,
  initialValues,
  width,
  title,
  selectList,
}) => {
  const [, setRoleList] = useState(selectList);
  const form = useRef(null);
  const { t } = useI18n();

  useEffect(() => {
    const getData = async () => {
      const arr = [];
      if (initialValues.objectId) {
        const children = await getChildren(initialValues.objectId);
        await getAllChild(children, arr);
      }
      const newList = getNewList(selectList, [...arr, initialValues.objectId]);
      setRoleList(newList);
    };
    if (selectList.length) {
      getData();
    }
  }, [initialValues.objectId, selectList]);

  const validateOnBlur = useCallback(
    async e => {
      e.preventDefault();
      const { value } = e.target;
      if (
        value.length < CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH ||
        value.length > CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH
      ) {
        form.current.setFieldError('name', t('message.workspace.keyLength'));
      } else if (!roleNameReg.test(value)) {
        form.current.setFieldError('name', t('validate.ValidateKey.keyContent'));
      } else {
        const query = new Parse.Query(Role);
        const findName = await query.equalTo('name', value.trim()).find();
        if (findName.length && initialValues?.name !== value) {
          form.current.setFieldError('name', t('validate.keyExist'));
        } else {
          form.current.setFieldError('name', null);
          delete form.current.errors.name;
        }
      }
    },
    [form, initialValues, t],
  );

  const validateNameOnBlur = useCallback(
    async e => {
      e.preventDefault();
      const value = e.target.value;
      const errorName = nameVerify(value);
      if (!value) {
        form.current.setFieldError('tag', t('pages.permission.roles.MembersModal.validate.tag.role'));
      } else if (
        value.length < CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH ||
        value.length > CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH
      ) {
        form.current.setFieldError(
          'tag',
          t('validate.fields.NAME_LENGTH_ERROR', {
            min: CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH,
            max: CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH,
          }),
        );
      } else if (errorName) {
        form.current.setFieldError('tag', errorName);
      } else {
        const query = new Parse.Query(Role);
        const findName = await query.equalTo('tag', value.trim()).find();
        if (findName.length && initialValues?.tag !== value) {
          form.current.setFieldError('tag', t('pages.permission.roles.MembersModal.validate.tag.exist'));
        } else {
          form.current.setFieldError('tag', null);
          delete form.current.errors.tag;
        }
      }
    },
    [form, initialValues, t],
  );

  const validate = async value => {
    const errors = {} as FormError;
    if (!value.name) {
      errors.name = addErrorMessage(errors.name, t('pages.permission.roles.MembersModal.validate.name'));
    } else if (form.current.errors?.name) {
      errors.name = form.current.errors.name;
    }
    if (!value.tag) {
      errors.tag = addErrorMessage(errors.tag, t('pages.permission.roles.MembersModal.validate.tag.role'));
    } else if (value.tag.trim() === '') {
      errors.tag = addErrorMessage(errors.tag, t('pages.permission.roles.MembersModal.validate.tag.noBlank'));
    } else if (form.current.errors?.tag) {
      errors.tag = form.current.errors.tag;
    }
    return errors;
  };

  return (
    <Formik innerRef={form} initialValues={{ ...initialValues }} onSubmit={onSubmit} validate={validate}>
      {({ handleSubmit }) => (
        <Modal
          maskClosable={false}
          confirmLoading={loading}
          width={width}
          title={title}
          open={visible}
          onOk={() => handleSubmit()}
          onCancel={onCancel}
        >
          <FormikFieldTouched />
          <FormField name="tag" label={t('global.name')} required>
            {({ field }) => (
              <Input
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH}
                placeholder={t('pages.permission.roles.MembersModal.FormField.placeholder.roleName', {
                  min: CHARACTER_SIZE.MIN_SYSTEM_NAME_LENGTH,
                  max: CHARACTER_SIZE.MAX_SYSTEM_NAME_LENGTH,
                })}
                {...field}
                onBlur={validateNameOnBlur}
              />
            )}
          </FormField>
          <FormField name="name" label={t('pages.permission.roles.MembersModal.FormField.name')} required>
            {({ field }) => (
              <Input
                disabled={title.indexOf(t('global.edit')) !== -1}
                maxLength={CHARACTER_SIZE.MAX_SYSTEM_KEY_LENGTH}
                placeholder={t('pages.permission.roles.MembersModal.FormField.placeholder.key', {
                  min: CHARACTER_SIZE.MIN_SYSTEM_KEY_LENGTH,
                  max: CHARACTER_SIZE.MAX_SYSTEM_KEY_LENGTH,
                })}
                {...field}
                onBlur={validateOnBlur}
              />
            )}
          </FormField>
          <FormField name="users" label={t('pages.permission.roles.MembersModal.FormField.users')}>
            {({ field }) => (
              <DebounceSelect
                mode="multiple"
                allowClear
                showSearch
                fetchOptions={getUserOptionsByName}
                fetchValues={getUserOptionsByValues}
                placeholder={t('pages.permission.roles.MembersModal.placeholder.0')}
                {...field}
              />
            )}
          </FormField>
          {/* <FormField name="role" label={t('pages.permission.roles.MembersModal.FormField.role')}>
            {({ field }) => (
              <TreeSelect
                disabled={title === t('global.columns.operation.addChild')}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                showSearch
                filterTreeNode={(value, option) =>
                  option.props?.title?.toLowerCase().indexOf(value?.toLowerCase()) > -1
                }
                placeholder={t('pages.permission.roles.MembersModal.placeholder.1')}
                treeDataSimpleMode
                allowClear
                treeDefaultExpandAll
                treeData={roleList}
                {...field}
              />
            )}
          </FormField> */}
        </Modal>
      )}
    </Formik>
  );
};

export default RolesModal;
